/**
 * SONE client — wire protocol.
 *
 * Mirrors packages/server/src/sync/protocol.ts. The duplication is deliberate:
 * the alternative is a shared package that both the server and the browser
 * bundle depend on, and pulling the server's module graph into the client
 * bundle to reuse a hundred lines of encoding is a worse trade.
 *
 * The two must stay in step. PROTOCOL_VERSION is negotiated at auth and a
 * mismatch is refused, so a drift shows up as a clear refusal rather than as
 * corrupted frames — which is the whole reason the version exists.
 */
import * as decoding from 'lib0/decoding';
import * as encoding from 'lib0/encoding';
export const PROTOCOL_VERSION = 1;
/**
 * The document format this client can read and write (ADR-0039).
 *
 * Sent at authentication and required to match the server's. A client whose
 * schema predates a block type does not ignore that block — y-prosemirror deletes
 * it from the shared document, for everyone — so being refused is the only safe
 * outcome, and it is a separate contract from the wire format above (ADR-0013).
 */
export { SCHEMA_VERSION as DOCUMENT_SCHEMA_VERSION } from '@sone/core';
export const ClientMessage = {
    Auth: 0,
    Open: 1,
    Sync: 2,
    Awareness: 3,
    Close: 4,
    Ping: 5,
};
export const ServerMessage = {
    AuthAck: 0,
    OpenAck: 1,
    Sync: 2,
    Awareness: 3,
    Closed: 4,
    Pong: 5,
    Error: 6,
    RoleChanged: 7,
    /**
     * Something this person's inbox counts has changed (ADR-0093).
     *
     * No handle: the only frame here that is about a person rather than a
     * document. No count either — the inbox holds the list it counts, and a
     * number on the wire would be a second answer to the question that list
     * already answers (ADR-0092).
     */
    Notify: 8,
};
export const NotifyScope = {
    Inbox: 'inbox',
    /**
     * The page tree of the workspace this connection authenticated for
     * (ADR-0096).
     *
     * Addressed by workspace rather than by person, and naming nothing about the
     * change: the tree route is the one place that decides what somebody sees.
     */
    Pages: 'pages',
    /**
     * The workspace's trash (ADR-0097).
     *
     * Its own scope rather than a second reason to refetch the tree: archiving
     * and restoring belong to both lists, renaming belongs only to the tree.
     */
    Trash: 'trash',
    /**
     * The workspace's shares screen (ADR-0098): links, granted, received.
     */
    Shares: 'shares',
};
export function encodeAuth(payload) {
    const e = encoding.createEncoder();
    encoding.writeVarUint(e, ClientMessage.Auth);
    encoding.writeVarString(e, JSON.stringify(payload));
    return encoding.toUint8Array(e);
}
export function encodeOpen(requestId, pageId) {
    const e = encoding.createEncoder();
    encoding.writeVarUint(e, ClientMessage.Open);
    encoding.writeVarUint(e, requestId);
    encoding.writeVarString(e, pageId);
    return encoding.toUint8Array(e);
}
export function encodeSync(handle, payload) {
    const e = encoding.createEncoder();
    encoding.writeVarUint(e, ClientMessage.Sync);
    encoding.writeVarUint(e, handle);
    encoding.writeVarUint8Array(e, payload);
    return encoding.toUint8Array(e);
}
export function encodeAwareness(handle, payload) {
    const e = encoding.createEncoder();
    encoding.writeVarUint(e, ClientMessage.Awareness);
    encoding.writeVarUint(e, handle);
    encoding.writeVarUint8Array(e, payload);
    return encoding.toUint8Array(e);
}
export function encodeCloseDocument(handle) {
    const e = encoding.createEncoder();
    encoding.writeVarUint(e, ClientMessage.Close);
    encoding.writeVarUint(e, handle);
    return encoding.toUint8Array(e);
}
export function encodePing() {
    const e = encoding.createEncoder();
    encoding.writeVarUint(e, ClientMessage.Ping);
    return encoding.toUint8Array(e);
}
export class ClientProtocolError extends Error {
    constructor(message) {
        super(message);
        this.name = 'ClientProtocolError';
    }
}
/**
 * Decode a server frame.
 *
 * Throws on anything unrecognised. A server speaking a newer protocol than the
 * client would otherwise be silently misread, and a half-decoded frame applied
 * to a document is worse than a dropped connection.
 */
export function decodeServerFrame(data) {
    if (data.byteLength === 0)
        throw new ClientProtocolError('empty frame');
    const d = decoding.createDecoder(data);
    let type;
    try {
        type = decoding.readVarUint(d);
    }
    catch {
        throw new ClientProtocolError('unreadable frame type');
    }
    try {
        switch (type) {
            case ServerMessage.AuthAck:
                return {
                    type: ServerMessage.AuthAck,
                    ack: JSON.parse(decoding.readVarString(d)),
                };
            case ServerMessage.OpenAck:
                return {
                    type: ServerMessage.OpenAck,
                    requestId: decoding.readVarUint(d),
                    handle: decoding.readVarUint(d),
                    role: decoding.readVarString(d),
                };
            case ServerMessage.Sync:
                return {
                    type: ServerMessage.Sync,
                    handle: decoding.readVarUint(d),
                    payload: decoding.readVarUint8Array(d),
                };
            case ServerMessage.Awareness:
                return {
                    type: ServerMessage.Awareness,
                    handle: decoding.readVarUint(d),
                    payload: decoding.readVarUint8Array(d),
                };
            case ServerMessage.Closed:
                return {
                    type: ServerMessage.Closed,
                    handle: decoding.readVarUint(d),
                    reason: decoding.readVarString(d),
                };
            case ServerMessage.RoleChanged:
                return {
                    type: ServerMessage.RoleChanged,
                    handle: decoding.readVarUint(d),
                    role: decoding.readVarString(d),
                };
            case ServerMessage.Notify:
                return { type: ServerMessage.Notify, scope: decoding.readVarString(d) };
            case ServerMessage.Pong:
                return { type: ServerMessage.Pong };
            case ServerMessage.Error:
                return {
                    type: ServerMessage.Error,
                    requestId: decoding.readVarUint(d),
                    code: decoding.readVarString(d),
                    detail: decoding.readVarString(d),
                };
            default:
                throw new ClientProtocolError(`unknown server frame type ${type}`);
        }
    }
    catch (err) {
        if (err instanceof ClientProtocolError)
            throw err;
        throw new ClientProtocolError('malformed frame');
    }
}
export const ROLE_ORDER = ['viewer', 'commenter', 'editor', 'admin'];
export const canWrite = (role) => ROLE_ORDER.indexOf(role) >= ROLE_ORDER.indexOf('editor');
/**
 * May this role say something, without changing anything.
 *
 * Its own question, because `commenter` had no consequence anywhere in the
 * client: `canWrite` answered the only question anybody asked, so the level
 * named after commenting was indistinguishable from `viewer` — the comment
 * button was gated on edit rights, with a note saying so "until there is a role
 * that separates them" (ADR-0046). This is the separation (ADR-0090).
 */
export const canComment = (role) => ROLE_ORDER.indexOf(role) >= ROLE_ORDER.indexOf('commenter');
//# sourceMappingURL=protocol.js.map